/**
 * 稼働機器 詳細モーダル。
 *
 * - リスト行クリックで開く
 * - 編集可能フィールドを更新 (updateSoldDevice callable)
 * - NES Health 判定された機器のみ NES Health 専用フィールドを表示
 * - 使用者・所有者 (親) の顧客名は顧客詳細画面へのリンク
 */
import type { ProductCategoryRecord } from '@/hooks/useProductCategories';
import { Save, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { useProductCategories } from '@/hooks/useProductCategories';
import {
  useUpdateSoldDevice,
  type SoldDeviceRecord,
  type UpdateSoldDevicePatch,
} from '@/hooks/useSalesOps';
import { paths } from '@/routes/paths';

interface Props {
  device: SoldDeviceRecord | null;
  contactMap: Map<string, string>;
  onOpenChange: (open: boolean) => void;
}

function tsToDateInput(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toISOString().slice(0, 10);
  }
  return '';
}

/**
 * NES Health 判定:
 * 1. productKind === 'NES Health' (bulkImport 由来で確実に設定済み)
 * 2. primaryCategoryId の ProductCategory.name が 'NES' 含み
 * 3. nesHealth フィールドに値が入っている (旧データ)
 */
function isNesHealthDevice(
  d: SoldDeviceRecord,
  categories: ProductCategoryRecord[] | undefined,
): boolean {
  if (d.productKind === 'NES Health') return true;
  if (d.primaryCategoryId && categories) {
    const cat = categories.find((c) => c.id === d.primaryCategoryId);
    if (cat?.name?.toLowerCase().includes('nes')) return true;
  }
  if (d.nesHealth && Object.values(d.nesHealth).some((v) => v != null)) {
    return true;
  }
  return false;
}

export function SoldDeviceDetailDialog({
  device,
  contactMap,
  onOpenChange,
}: Props) {
  const update = useUpdateSoldDevice();
  const { data: categories } = useProductCategories();

  const isNes = useMemo(
    () => (device ? isNesHealthDevice(device, categories) : false),
    [device, categories],
  );

  // 編集状態 (device が変わるたびに reset)
  const [serialNumber, setSerialNumber] = useState('');
  const [productName, setProductName] = useState('');
  const [modelName, setModelName] = useState('');
  const [variation, setVariation] = useState('');
  const [productKind, setProductKind] = useState('');
  const [remarks, setRemarks] = useState('');
  const [installedAt, setInstalledAt] = useState('');
  const [warrantyStartAt, setWarrantyStartAt] = useState('');
  const [warrantyEndAt, setWarrantyEndAt] = useState('');
  const [serviceTag, setServiceTag] = useState('');
  const [expressCode, setExpressCode] = useState('');

  // NES Health 固有
  const [nesDeviceKind, setNesDeviceKind] = useState('');
  const [nesMind, setNesMind] = useState<boolean | null>(null);
  const [nesMr, setNesMr] = useState<boolean | null>(null);
  const [nesSmi, setNesSmi] = useState<boolean | null>(null);
  const [nesBwsEmail, setNesBwsEmail] = useState('');
  const [nesShippedAt, setNesShippedAt] = useState('');
  const [nesReceivedAt, setNesReceivedAt] = useState('');

  useEffect(() => {
    if (!device) return;
    setSerialNumber(device.serialNumber ?? '');
    setProductName(device.productName ?? '');
    setModelName(device.modelName ?? '');
    setVariation(device.variation ?? '');
    setProductKind(device.productKind ?? '');
    setRemarks(device.remarks ?? '');
    setInstalledAt(tsToDateInput(device.installedAt));
    setWarrantyStartAt(tsToDateInput(device.warrantyStartAt));
    setWarrantyEndAt(tsToDateInput(device.warrantyEndAt));
    setServiceTag(device.serviceTag ?? '');
    setExpressCode(device.expressCode ?? '');
    const nh = device.nesHealth ?? {};
    setNesDeviceKind(nh.deviceKind ?? '');
    setNesMind(nh.mind ?? null);
    setNesMr(nh.mr ?? null);
    setNesSmi(nh.smi ?? null);
    setNesBwsEmail(nh.bwsEmail ?? '');
    setNesShippedAt(nh.shippedAt ?? '');
    setNesReceivedAt(nh.receivedAt ?? '');
  }, [device]);

  if (!device) return null;

  const handleSave = async () => {
    const patch: UpdateSoldDevicePatch = {
      serialNumber,
      productName,
      modelName,
      variation: variation || null,
      productKind: productKind || null,
      remarks: remarks || null,
      installedAt,
      warrantyStartAt: warrantyStartAt || null,
      warrantyEndAt: warrantyEndAt || null,
      serviceTag: serviceTag || null,
      expressCode: expressCode || null,
    };
    if (isNes) {
      patch.nesHealth = {
        deviceKind: nesDeviceKind || null,
        mind: nesMind,
        mr: nesMr,
        smi: nesSmi,
        bwsEmail: nesBwsEmail || null,
        shippedAt: nesShippedAt || null,
        receivedAt: nesReceivedAt || null,
      };
    }
    try {
      await update.mutateAsync({ soldDeviceId: device.id, patch });
      toast.success('稼働機器を更新しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const userName = contactMap.get(device.contactId) ?? device.contactId;
  const ownerName = device.ownerContactId
    ? contactMap.get(device.ownerContactId) ?? device.ownerContactId
    : null;
  const hasSeparateOwner =
    device.ownerContactId && device.ownerContactId !== device.contactId;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            稼働機器の詳細
            {device.decommissioned && (
              <span className="ml-2 text-xs font-normal text-[var(--accent-red)]">
                (アーカイブ済)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 顧客情報 (読み取り専用 + リンク) */}
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 text-xs">
            <div className="mb-2 font-semibold">紐付け顧客</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <div>
                <span className="text-[var(--text-muted)]">
                  {hasSeparateOwner ? '使用者: ' : '所有: '}
                </span>
                <Link
                  to={paths.salesContactDetail.replace(':contactId', device.contactId)}
                  className="text-[var(--accent)] hover:underline"
                  onClick={() => onOpenChange(false)}
                >
                  {userName}
                </Link>
              </div>
              {hasSeparateOwner && ownerName && device.ownerContactId && (
                <div>
                  <span className="text-[var(--text-muted)]">所有 (親): </span>
                  <Link
                    to={paths.salesContactDetail.replace(
                      ':contactId',
                      device.ownerContactId,
                    )}
                    className="text-[var(--accent-purple)] hover:underline"
                    onClick={() => onOpenChange(false)}
                  >
                    {ownerName}
                  </Link>
                </div>
              )}
            </div>
            <p className="mt-1 text-[10px] text-[var(--text-muted)]">
              所有者変更は譲渡操作 (一覧の譲渡ボタン) から
            </p>
          </div>

          {/* 編集可能フィールド */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">商品名</Label>
              <Input
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">モデル名</Label>
              <Input
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">バリエーション</Label>
              <Input
                value={variation}
                onChange={(e) => setVariation(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">種別</Label>
              <Input
                value={productKind}
                onChange={(e) => setProductKind(e.target.value)}
                placeholder="TimeWaver本体 / NES Health 等"
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">シリアル番号</Label>
              <Input
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                className="font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">設置日</Label>
              <Input
                type="date"
                value={installedAt}
                onChange={(e) => setInstalledAt(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">保証開始日</Label>
              <Input
                type="date"
                value={warrantyStartAt}
                onChange={(e) => setWarrantyStartAt(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">保証終了日</Label>
              <Input
                type="date"
                value={warrantyEndAt}
                onChange={(e) => setWarrantyEndAt(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">サービスタグ</Label>
              <Input
                value={serviceTag}
                onChange={(e) => setServiceTag(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">エクスプレスコード</Label>
              <Input
                value={expressCode}
                onChange={(e) => setExpressCode(e.target.value)}
                className="text-sm"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">備考</Label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-input)] border border-[var(--border)] rounded-sm"
            />
          </div>

          {/* NES Health 固有フィールド */}
          {isNes && (
            <div className="rounded-md border border-[var(--accent-purple)] bg-[var(--accent-purple-subtle,_rgba(155,93,229,0.08))] p-3 space-y-3">
              <div className="text-xs font-semibold text-[var(--accent-purple)]">
                NES Health 専用フィールド
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">機器種類</Label>
                  <Input
                    value={nesDeviceKind}
                    onChange={(e) => setNesDeviceKind(e.target.value)}
                    placeholder="マイヘルス / BWSアカウント 等"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">BWS アカウントアドレス</Label>
                  <Input
                    type="email"
                    value={nesBwsEmail}
                    onChange={(e) => setNesBwsEmail(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">出荷日</Label>
                  <Input
                    type="date"
                    value={nesShippedAt}
                    onChange={(e) => setNesShippedAt(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">入荷日</Label>
                  <Input
                    type="date"
                    value={nesReceivedAt}
                    onChange={(e) => setNesReceivedAt(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-4 text-xs">
                {(
                  [
                    ['Mind', nesMind, setNesMind],
                    ['MR', nesMr, setNesMr],
                    ['SMI', nesSmi, setNesSmi],
                  ] as const
                ).map(([label, val, setter]) => (
                  <label
                    key={label}
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={val === true}
                      ref={(el) => {
                        if (el) el.indeterminate = val === null;
                      }}
                      onChange={(e) => setter(e.target.checked)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* 譲渡履歴 (読み取り) */}
          {device.transferHistory && device.transferHistory.length > 0 && (
            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 text-xs">
              <div className="mb-1 font-semibold">
                譲渡履歴 ({device.transferHistory.length} 回)
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {device.transferHistory.map((h, idx) => (
                  <div key={idx} className="text-[var(--text-muted)]">
                    {tsToDateInput(h.transferredAt)}: {contactMap.get(h.fromContactId) ?? h.fromContactId} →{' '}
                    {contactMap.get(h.toContactId) ?? h.toContactId}
                    {h.reason && <span className="ml-1">「{h.reason}」</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            <X className="size-3.5" />
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={update.isPending}
          >
            <Save className="size-3.5" />
            {update.isPending ? '保存中…' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
